import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { api, ApiError } from '~/lib/api';
import { cn } from '~/lib/cn';
import { SCOPE_GROUPS, isCidrLike, type Scope } from '~/lib/scopes';
import { SlideOver } from '~/components/ui/SlideOver';

interface MintResponse {
  id: string;
  prefix: string;
  secret: string;
  label: string;
  mode: 'live' | 'test';
  scopes: readonly string[];
  cidrs: readonly string[];
  createdAt: string;
}

interface NewKeyPanelProps {
  open: boolean;
  onClose: () => void;
  /** Fires once the key is minted so the parent can refresh its list. */
  onMinted: (key: MintResponse) => void;
}

type Mode = 'live' | 'test';

/**
 * The one place a tenant goes from "want an integration" to "have credentials
 * I can paste into my backend." Two stages:
 *   1. Form   — label, scopes, optional CIDR allowlist
 *   2. Reveal — the sk_live_… secret shown once; the server never returns it
 *               again. The copy affordance is deliberate and loud.
 */
export function NewKeyPanel({ open, onClose, onMinted }: NewKeyPanelProps): JSX.Element {
  const [label, setLabel] = useState('');
  const [mode] = useState<Mode>('live'); // sandbox refused by backend for now
  const [selected, setSelected] = useState<Set<Scope>>(new Set());
  const [cidrs, setCidrs] = useState<string[]>([]);
  const [cidrDraft, setCidrDraft] = useState('');
  const [cidrError, setCidrError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minted, setMinted] = useState<MintResponse | null>(null);

  function reset(): void {
    setLabel('');
    setSelected(new Set());
    setCidrs([]);
    setCidrDraft('');
    setCidrError(null);
    setError(null);
    setMinted(null);
    setSubmitting(false);
  }

  function closeAndReset(): void {
    reset();
    onClose();
  }

  function toggleScope(scope: Scope): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (scope === '*') {
        if (next.has('*')) next.delete('*');
        else {
          next.clear();
          next.add('*');
        }
        return next;
      }
      // Picking a specific scope implicitly cancels the superset.
      next.delete('*');
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  }

  function addCidr(): void {
    const v = cidrDraft.trim();
    if (v === '') return;
    if (!isCidrLike(v)) {
      setCidrError(`"${v}" doesn't look like a CIDR (e.g. 203.0.113.0/24).`);
      return;
    }
    if (cidrs.includes(v)) {
      setCidrError('Already in the list.');
      return;
    }
    if (cidrs.length >= 16) {
      setCidrError('Max 16 CIDRs per key.');
      return;
    }
    setCidrs([...cidrs, v]);
    setCidrDraft('');
    setCidrError(null);
  }

  function removeCidr(cidr: string): void {
    setCidrs(cidrs.filter((c) => c !== cidr));
  }

  function onCidrKey(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addCidr();
    }
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (label.trim() === '') {
      setError('Give the key a label — where will it be used?');
      return;
    }
    if (selected.size === 0) {
      setError('Pick at least one scope. The "All scopes" shortcut grants everything.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api<MintResponse>('/dashboard/api-keys', {
        method: 'POST',
        body: {
          label: label.trim(),
          mode,
          scopes: Array.from(selected),
          cidrs,
        },
      });
      setMinted(res);
      onMinted(res);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not mint the key. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (minted) {
    return (
      <SlideOver
        open={open}
        onClose={closeAndReset}
        title="Key minted"
        description="Copy the secret now. We hash it the moment this panel closes and cannot show it to you again."
      >
        <RevealPanel secret={minted.secret} prefix={minted.prefix} onDone={closeAndReset} />
      </SlideOver>
    );
  }

  return (
    <SlideOver
      open={open}
      onClose={closeAndReset}
      title="New key"
      description="Scoped credentials your service uses to call Seven."
      footer={
        <div className="flex flex-col gap-2">
          {error && (
            // Lives in the footer, next to the button the user just pressed,
            // so a mint failure (TENANT_NOT_ACTIVE, SANDBOX_UNAVAILABLE, bad
            // CIDR from the server, 429) is impossible to miss — the inline
            // version at the bottom of a long form was below the fold.
            <div
              role="alert"
              className="rounded border border-stop/20 bg-stop-tint px-3 py-2 text-[13.5px] leading-[18px] text-stop"
            >
              {error}
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={closeAndReset}
              className="h-9 rounded-md px-3 text-[14px] font-medium text-ink-secondary hover:bg-surface-margin hover:text-ink-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="new-key-form"
              disabled={submitting}
              className="inline-flex h-9 items-center justify-center rounded-md bg-ink-primary px-4 text-[14px] font-semibold text-ink-on-dark hover:bg-ink-primary/90 disabled:opacity-60"
            >
              {submitting ? 'Minting…' : 'Mint key'}
            </button>
          </div>
        </div>
      }
    >
      <form id="new-key-form" onSubmit={onSubmit} className="flex flex-col gap-6">
        <Field label="Label" hint="Shown in the keys list. 'webhook-production' is better than 'key 1'.">
          <input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="webhook-production"
            className="h-9 w-full rounded border border-rule-divider bg-surface-margin px-3 text-[14.5px] text-ink-primary focus:border-ink-primary focus:bg-surface-slip focus:outline-none focus:ring-2 focus:ring-ink-primary/15"
          />
        </Field>

        <section>
          <SectionLabel label="Scopes" hint="What the key is allowed to do." />
          <SupersetToggle
            active={selected.has('*')}
            onToggle={() => toggleScope('*')}
          />
          <div className="mt-3 flex flex-col gap-4">
            {SCOPE_GROUPS.map((group) => (
              <ScopeGroupBlock
                key={group.label}
                group={group}
                selected={selected}
                supersetOn={selected.has('*')}
                onToggle={toggleScope}
              />
            ))}
          </div>
        </section>

        <section>
          <SectionLabel
            label="Allowed IPs"
            hint="Optional. If set, Seven only honors this key when the client IP matches."
          />
          <div className="flex gap-2">
            <input
              value={cidrDraft}
              onChange={(e) => {
                setCidrDraft(e.target.value);
                setCidrError(null);
              }}
              onKeyDown={onCidrKey}
              placeholder="203.0.113.0/24"
              className="h-9 flex-1 rounded border border-rule-divider bg-surface-margin px-3 font-mono text-[13.5px] text-ink-primary focus:border-ink-primary focus:bg-surface-slip focus:outline-none focus:ring-2 focus:ring-ink-primary/15"
            />
            <button
              type="button"
              onClick={addCidr}
              className="h-9 rounded-md border border-rule-divider bg-surface-slip px-3 text-[14px] font-medium text-ink-primary hover:bg-surface-margin"
            >
              Add
            </button>
          </div>
          {cidrError && (
            <p className="mt-1.5 text-[13px] text-stop">{cidrError}</p>
          )}
          {cidrs.length > 0 && (
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {cidrs.map((cidr) => (
                <li key={cidr}>
                  <button
                    type="button"
                    onClick={() => removeCidr(cidr)}
                    className="font-mono inline-flex items-center gap-1 rounded border border-rule-divider bg-surface-slip px-2 py-[3px] text-[13px] text-ink-secondary hover:border-stop/40 hover:bg-stop-tint hover:text-stop"
                    aria-label={`Remove ${cidr}`}
                  >
                    {cidr}
                    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
                      <path d="M3 3l10 10M13 3L3 13" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {cidrs.length === 0 && (
            <p className="mt-2 text-[13px] italic text-ink-watermark">
              No restriction — the key will work from any IP.
            </p>
          )}
        </section>
      </form>
    </SlideOver>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13.5px] font-medium text-ink-primary">{label}</span>
      {hint && <span className="text-[11.5px] text-ink-watermark">{hint}</span>}
      {children}
    </label>
  );
}

function SectionLabel({ label, hint }: { label: string; hint?: string }): JSX.Element {
  return (
    <div className="mb-2">
      <div className="text-[13.5px] font-medium text-ink-primary">{label}</div>
      {hint && <div className="text-[11.5px] text-ink-watermark">{hint}</div>}
    </div>
  );
}

function SupersetToggle({
  active,
  onToggle,
}: {
  active: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={cn(
        'flex w-full items-start justify-between gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
        active
          ? 'border-ink-primary bg-ink-primary text-ink-on-dark'
          : 'border-rule-divider bg-surface-slip text-ink-primary hover:border-ink-primary/40',
      )}
    >
      <div>
        <div className="text-[14px] font-semibold">All scopes (*)</div>
        <div className={cn('mt-0.5 text-[11.5px]', active ? 'text-white/70' : 'text-ink-watermark')}>
          Grants every current and future scope. Use for root/service keys only.
        </div>
      </div>
      <Checkmark on={active} tone="dark" />
    </button>
  );
}

function ScopeGroupBlock({
  group,
  selected,
  supersetOn,
  onToggle,
}: {
  group: (typeof SCOPE_GROUPS)[number];
  selected: Set<Scope>;
  supersetOn: boolean;
  onToggle: (s: Scope) => void;
}): JSX.Element {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <div className="text-[11.5px] font-medium uppercase tracking-[0.08em] text-ink-watermark">
          {group.label}
        </div>
        <div className="text-[11px] text-ink-watermark">{group.description}</div>
      </div>
      <div className="flex flex-col gap-1 rounded-md border border-rule-hairline bg-surface-slip">
        {group.scopes.map((entry) => {
          const checked = supersetOn || selected.has(entry.scope);
          return (
            <button
              key={entry.scope}
              type="button"
              onClick={() => onToggle(entry.scope)}
              aria-pressed={checked}
              disabled={supersetOn}
              className={cn(
                'flex items-start justify-between gap-3 border-b border-rule-hairline px-3 py-2 text-left last:border-b-0',
                supersetOn ? 'cursor-not-allowed opacity-55' : 'hover:bg-surface-margin',
              )}
            >
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[14px] font-medium text-ink-primary">
                    {entry.label}
                  </span>
                  <span className="font-mono rounded bg-surface-margin px-1.5 py-[1px] text-[10.5px] text-ink-secondary">
                    {entry.scope}
                  </span>
                </div>
                <div className="mt-0.5 text-[11.5px] text-ink-watermark">
                  {entry.detail}
                </div>
              </div>
              <Checkmark on={checked} tone="light" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Checkmark({ on, tone }: { on: boolean; tone: 'light' | 'dark' }): JSX.Element {
  return (
    <span
      className={cn(
        'mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded border',
        on
          ? tone === 'dark'
            ? 'border-white/40 bg-white text-ink-primary'
            : 'border-ink-primary bg-ink-primary text-ink-on-dark'
          : 'border-rule-divider bg-surface-slip text-transparent',
      )}
      aria-hidden
    >
      <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        <path d="M3.5 8.5l3 3 6-6" />
      </svg>
    </span>
  );
}

function RevealPanel({
  secret,
  prefix,
  onDone,
}: {
  secret: string;
  prefix: string;
  onDone: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard denied — select the text instead so the user can Cmd-C.
      const el = document.getElementById('mint-secret') as HTMLInputElement | null;
      el?.select();
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-md border border-notice/30 bg-notice-tint px-3 py-2.5 text-[13.5px] leading-[18px] text-notice">
        This is the only time Seven shows the full secret. Store it somewhere
        your service can read, then close this panel.
      </div>

      <div>
        <div className="mb-1.5 text-[13.5px] font-medium text-ink-primary">
          Secret
        </div>
        <div className="flex items-stretch gap-2">
          <input
            id="mint-secret"
            readOnly
            value={secret}
            onFocus={(e) => e.currentTarget.select()}
            className="font-mono h-10 flex-1 rounded border border-rule-divider bg-surface-margin px-3 text-[13.5px] text-ink-primary focus:border-ink-primary focus:outline-none focus:ring-2 focus:ring-ink-primary/15"
          />
          <button
            type="button"
            onClick={copy}
            className={cn(
              'inline-flex h-10 items-center justify-center rounded-md px-3 text-[14px] font-semibold text-ink-on-dark',
              copied ? 'bg-tally' : 'bg-ink-primary hover:bg-ink-primary/90',
            )}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <div className="font-mono mt-1 text-[11.5px] text-ink-watermark">
          Prefix on record: {prefix}···
        </div>
      </div>

      <button
        type="button"
        onClick={onDone}
        className="self-start text-[14px] font-medium text-ink-primary underline-offset-4 hover:underline"
      >
        I've saved it — close
      </button>
    </div>
  );
}
